import type { CaseStudy } from '../types';

export const tsmc: CaseStudy = {
  id: 'tsmc',
  order: 2,
  title: '台积电',
  english: 'TSMC',
  category: 'technology',
  era: '1987 — 今',
  region: '中国台湾 / 全球',
  accent: '#367a6a',
  hook: '一家不设计芯片的公司，却成了全球科技业最难替代的节点之一。它卖的不是晶圆，而是「把设计变成可量产芯片」的确定性。',
  question: '为什么最先进的芯片设计公司，愿意把核心图纸交给一家代工厂？',
  summary:
    '台积电把「中立代工」变成一种制度安排：不与客户竞争，换取客户共享设计；再用良率学习、生态协同和巨额资本开支，把信任变成难以复制的制造能力。',
  thesis: '技术领先不只来自一项工艺，而来自「信任结构 × 学习速度 × 资本密度」的叠加。',
  quote: '它最重要的产品不是某一代制程，而是让客户相信：把最难的图纸交出去，不会被拿去武装竞争对手。',
  entryNodeIds: ['tsmc-neutral', 'tsmc-learning', 'tsmc-capex'],
  focusNodeId: 'tsmc-neutral',
  lensIds: ['scale', 'lockin', 'standards', 'scarcity'],
  opportunityIds: ['opp-advanced-packaging', 'opp-mature-node'],
  sourceIds: ['tsmc-4q24', 'tsmc-ar-2024'],
  facts: [
    {
      text: '台积电成立于 1987 年，采用「纯晶圆代工」模式：只替客户制造芯片，不设计、不销售自有品牌芯片。',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      text: '2024 年第四季，7 纳米及以下先进制程占台积电晶圆收入的 74%；其中 3 纳米占 26%，5 纳米占 34%，7 纳米占 14%。',
      sourceIds: ['tsmc-4q24'],
    },
    {
      text: '先进制程需要极高的资本开支与产能利用率；2024 年公司资本开支约 298 亿美元，折旧与产能爬坡会显著影响毛利。',
      sourceIds: ['tsmc-ar-2024'],
    },
  ],
  nodes: [
    {
      id: 'tsmc',
      caseId: 'tsmc',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '台积电',
      summary: '把「中立制造」做成全球科技基础设施。',
      detail:
        '台积电不是从消费品牌起家，而是从产业分工的缝隙切入：芯片设计公司越来越多，但建一座先进晶圆厂越来越贵。它选择只做制造、不与客户竞争，从而把「代工」从低端外包变成战略基础设施。',
      boundary: '这个模型高度依赖全球分工、技术保护与稳定基础设施，不是所有制造业都能复制。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-neutral',
      caseId: 'tsmc',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '中立代工：不跟客户抢生意',
      summary: '只制造、不设计自有品牌芯片，降低客户对「技术被复制」的担忧。',
      detail:
        '如果代工厂同时卖自己的芯片，客户就要担心设计被学习、产能被优先分配给自己。纯代工模式用「不竞争」换取信任，让无晶圆厂设计公司愿意把最先进的设计交出来。信任在这里不是道德，而是可降低交易成本的制度设计。',
      boundary: '中立承诺必须靠治理、知识产权保护和长期行为记录维持；一次利益冲突就可能摧毁信任。',
      counter: '三星等同时做设计与代工的模式也能成功，但客户关系与保密安排更复杂。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-learning',
      caseId: 'tsmc',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '良率学习曲线',
      summary: '量产越多，工艺数据越多，良率与交付确定性越强。',
      detail:
        '先进制程的难点不只是「做出第一片」，而是让成千上万片晶圆稳定达到高良率。每一批量产都会产生工艺数据，帮助工程团队调整参数、减少缺陷。客户越早采用、产量越大，学习循环越快，下一代制程也更有资金与经验。',
      boundary: '学习曲线需要稳定需求、顶尖工程团队和设备供应；需求断档会让巨额产能闲置。',
      counter: '在成熟制程，后来者可以购买设备、复制流程，学习曲线的壁垒明显低于先进制程。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-ecosystem',
      caseId: 'tsmc',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '生态协同：把接口变成护城河',
      summary: 'EDA、IP、设备、材料与封装伙伴围绕同一套工艺协同，降低客户整合成本。',
      detail:
        '芯片设计不是把图纸交给工厂就结束。设计工具、IP 模块、封装、测试、设备参数都要与制程匹配。台积电通过开放创新平台与联盟，把大量互补方拉进同一套接口，客户迁移到别处时，不只要换工厂，还要重建一整条协作链。',
      boundary: '生态越强，越依赖单一代工节点的稳定；地缘或技术冲击会把协同优势变成集中风险。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-capex',
      caseId: 'tsmc',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '资本密度：用折旧筑墙',
      summary: '一座先进晶圆厂动辄百亿美元级，只有足够大的订单才能摊薄成本。',
      detail:
        '先进制程设备昂贵、技术迭代快，资本开支必须靠高产能利用率回收。这形成双重门槛：新进入者要一次性拿出巨额资本，还要找到足够多愿意预付信任的客户。对领先者，规模与现金流互相加固；对追赶者，折旧与良率爬坡会持续消耗现金。',
      boundary: '资本壁垒只在技术路线相对连续时有效；范式变化可能让旧设备一夜贬值。',
      counter: '成熟制程或特色工艺可以用更低的资本密度竞争，不必参与最先进节点的军备竞赛。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-condition',
      caseId: 'tsmc',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：全球分工与稳定基础设施',
      summary: '设计、设备、材料、制造分布在不同经济体，靠贸易、知识产权与物流连接。',
      detail:
        '台积电的模型建立在全球化分工之上：美国的设计工具与客户、荷兰的光刻设备、日本的材料、台湾的制造与工程人才。稳定电力、水资源、物流和地缘环境，都是「制造确定性」的一部分。',
      boundary: '出口管制、关税、战争风险或关键设备断供，会直接改变这套分工的成本与可行性。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-outcome',
      caseId: 'tsmc',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：先进制程的默认节点',
      summary: '客户、生态与资本互相锁定，使领先制程成为全行业的基础设施。',
      detail:
        '当大多数顶尖设计都围绕同一套制程优化，台积电就不只是供应商，而是产业标准的一部分。它的议价权来自「换掉它需要重建整条协作链」，而不是单纯的报价更低。',
      boundary: '这种地位集中在最先进节点；成熟制程竞争更分散，价格周期性更强。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['tsmc-4q24', 'tsmc-ar-2024'],
    },
    {
      id: 'tsmc-geo',
      caseId: 'tsmc',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：地理集中',
      summary: '最先进的产能与工程能力高度集中，地缘或自然灾害会放大全球影响。',
      detail:
        '效率最高的布局，往往也是风险最集中的布局。台积电在海外建厂可以分散风险，但会面对成本、人才、供应链与良率爬坡的挑战。全球化退潮时，「最优成本」要让位于「可生存的冗余」。',
      boundary: '集中不一定导致事故；但一旦发生冲击，替代产能的建设周期以年计。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-cycle',
      caseId: 'tsmc',
      kind: 'risk',
      stage: 4,
      lane: 0,
      title: '脆弱处：资本周期',
      summary: '先进制程的巨额投资，会在需求放缓时变成折旧与闲置产能。',
      detail:
        '半导体需求有库存周期。景气时客户抢产能、公司扩产；景气反转时，订单下修与折旧同时出现，毛利承压。越先进的节点，资本开支越大，周期摆幅也越明显。',
      boundary: '长期需求增长可以吸收短期波动，但需要准确的产能规划与客户结构。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['tsmc-ar-2024'],
    },
    {
      id: 'tsmc-opp',
      caseId: 'tsmc',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：给「瓶颈」做配套',
      summary: '不必造晶圆厂，也可以服务于先进封装、测试、散热、材料回收与供应链本地化。',
      detail:
        '当制造节点成为瓶颈，价值会外溢到周围的「补缺口」环节：先进封装与测试、洁净室工程、超纯水与化学品回收、设备维护、人才培训、合规与溯源。对普通创业者，机会通常不在最资本密集的核心，而在核心周围的专业化服务。',
      boundary: '这些服务同样有认证、资本与技术门槛；客户集中度高，账期长，不能只看行业热度。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['tsmc-ar-2024', 'tsmc-4q24'],
    },
  ],
  edges: [
    {
      id: 'tsmc-e1',
      caseId: 'tsmc',
      from: 'tsmc',
      to: 'tsmc-neutral',
      label: '用不竞争换信任',
      explanation: '纯代工定位让无晶圆厂设计公司更愿意共享核心设计。',
      tone: 'cause',
    },
    {
      id: 'tsmc-e2',
      caseId: 'tsmc',
      from: 'tsmc',
      to: 'tsmc-learning',
      label: '量产驱动学习',
      explanation: '持续接单与扩产，让工艺数据、良率和工程经验形成循环。',
      tone: 'cause',
    },
    {
      id: 'tsmc-e3',
      caseId: 'tsmc',
      from: 'tsmc-neutral',
      to: 'tsmc-ecosystem',
      label: '信任吸引互补方',
      explanation: '客户愿意围绕同一代工平台投入设计、IP 与封装协同。',
      tone: 'enable',
    },
    {
      id: 'tsmc-e4',
      caseId: 'tsmc',
      from: 'tsmc-learning',
      to: 'tsmc-capex',
      label: '良率支撑重资产',
      explanation: '高良率与稳定订单让巨额设备投资具备回收可能，后来者却要同时承担学习与折旧。',
      tone: 'cause',
    },
    {
      id: 'tsmc-e5',
      caseId: 'tsmc',
      from: 'tsmc-ecosystem',
      to: 'tsmc-outcome',
      label: '迁移不只是换工厂',
      explanation: '设计工具、IP、封装和测试都围绕同一平台优化，客户迁移要重建协作链。',
      tone: 'cause',
    },
    {
      id: 'tsmc-e6',
      caseId: 'tsmc',
      from: 'tsmc-capex',
      to: 'tsmc-outcome',
      label: '资本门槛',
      explanation: '资本密度与折旧压力，使少数领先者能持续参与最先进节点的竞争。',
      tone: 'cause',
    },
    {
      id: 'tsmc-e7',
      caseId: 'tsmc',
      from: 'tsmc-condition',
      to: 'tsmc-outcome',
      label: '分工提供前提',
      explanation: '全球化的设备、材料、设计与贸易网络，让「只做制造」可以做到世界级。',
      tone: 'enable',
    },
    {
      id: 'tsmc-e8',
      caseId: 'tsmc',
      from: 'tsmc-outcome',
      to: 'tsmc-geo',
      label: '效率带来集中',
      explanation: '最优成本布局把先进产能与工程人才集中在少数地点，冲击会外溢到全球客户。',
      tone: 'risk',
    },
    {
      id: 'tsmc-e9',
      caseId: 'tsmc',
      from: 'tsmc-outcome',
      to: 'tsmc-cycle',
      label: '重资产放大周期',
      explanation: '需求下修时，折旧、闲置产能与客户库存调整同时出现。',
      tone: 'risk',
    },
    {
      id: 'tsmc-e10',
      caseId: 'tsmc',
      from: 'tsmc-outcome',
      to: 'tsmc-opp',
      label: '瓶颈外溢机会',
      explanation: '核心制造越关键，周围的封装、材料、维护与合规服务越有专业化空间。',
      tone: 'cause',
    },
    {
      id: 'tsmc-e11',
      caseId: 'tsmc',
      from: 'tsmc-geo',
      to: 'tsmc-opp',
      label: '分散化创造需求',
      explanation: '供应链本地化与冗余建设，会带来新的工程、认证与运营服务需求。',
      tone: 'enable',
    },
  ],
};
